/**
 * Инпут (§2.3 «Инпут», §5.2 «Что происходит по клику на ячейку»).
 *
 * Размер `m` — поле формы 32 px, `cell` — поле в ячейке таблицы 28 px: ровно по
 * высоте строки, чтобы сетка не дёргалась при входе в правку.
 *
 * Ошибка показывается строкой под полем и остаётся на месте — не тултипом и не
 * всплывашкой (П6). Поле получает `aria-invalid` и `aria-describedby` на эту
 * строку, иначе ошибка существует только для зрячих.
 *
 * `trailing` — слот для иконки «открыть ↗» в поле URL и для кнопки-календаря в
 * поле даты: нормализацию URL и разбор даты делает владелец поля, не контрол.
 */
import { forwardRef, useId } from 'react'
import type { InputHTMLAttributes, ReactNode } from 'react'
import './Input.css'

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** m — 32 px (по умолчанию), cell — 28 px в строке таблицы. */
  size?: 'm' | 'cell'
  invalid?: boolean
  /** Текст ошибки под полем. Включает невалидное состояние сам. */
  error?: string
  /** Серая строка-последствие или подсказка под полем (П6). */
  hint?: ReactNode
  /** Выравнивание направо + табличные цифры: числовые поля (§5.2). */
  numeric?: boolean
  iconBefore?: ReactNode
  /** Правый слот: «открыть ↗», кнопка календаря, счётчик символов. */
  trailing?: ReactNode
  /** Правый слот — ссылка: красится в --link. */
  trailingIsLink?: boolean
  className?: string
}

export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(
  {
    size = 'm',
    invalid = false,
    error,
    hint,
    numeric = false,
    iconBefore,
    trailing,
    trailingIsLink = false,
    disabled = false,
    className,
    id,
    'aria-describedby': describedBy,
    ...rest
  },
  ref,
) {
  const reactId = useId()
  const inputId = id ?? `input${reactId}`
  const errorId = `${inputId}-error`
  const hintId = `${inputId}-hint`
  const isInvalid = invalid || Boolean(error)

  const classes = ['field', `field--${size}`]
  if (isInvalid) classes.push('field--invalid')
  if (disabled) classes.push('field--disabled')
  if (numeric) classes.push('field--numeric')
  if (className) classes.push(className)

  const described = [describedBy, error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(' ')

  return (
    <div className={classes.join(' ')}>
      <div className="field__control">
        {iconBefore ? <span className="field__lead">{iconBefore}</span> : null}
        <input
          ref={ref}
          id={inputId}
          className="field__input t-cell"
          disabled={disabled}
          aria-invalid={isInvalid || undefined}
          aria-describedby={described || undefined}
          {...rest}
        />
        {trailing ? (
          <span className={trailingIsLink ? 'field__trail field__trail--link' : 'field__trail'}>
            {trailing}
          </span>
        ) : null}
      </div>
      {error ? (
        <span id={errorId} className="field__error t-meta">
          {error}
        </span>
      ) : null}
      {hint ? (
        <span id={hintId} className="field__hint t-meta">
          {hint}
        </span>
      ) : null}
    </div>
  )
})
